import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { SituacaoVendedorEnum } from '@shared/enums/pessoa/situacao-vendedor.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { OportunidadeService } from '@shared/services/apis/vendas/oportunidade.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { LookupOportunidade } from '@shared/services/models/vendas/oportunidade.model';
import { CriarOportunidadeVendaRequest } from '@shared/services/requests/vendas/criar-oportunidade-venda.request';
import { VendedorLookupStruct } from '@shared/services/structs/pessoa/vendedor-lookup.struct';
import { OportunidadeResumoStruct } from '@shared/services/structs/vendas/oportunidade-resumo.struct';

/** Documento de Vendas gravado (orçamento ou pedido) sem oportunidade. */
export type CriarOportunidadeDocumentoDialogData = {
  idVenda: number;
  idVendaTipoDocumento: number;
  numero: number;
  idPessoaCliente: number;
  nomeCliente: string;
  /** Vendedor do documento; sem ele, o usuário escolhe o responsável (RX-01, CA-39). */
  idVendedor: number | null;
  /** Vincular exige Alterar do CRM e do Processo de Venda: sem elas, o "Vincular a ela" não aparece. */
  podeVincular: boolean;
};

export type CriarOportunidadeDocumentoDialogResult = {
  /** Oportunidade criada e vinculada ao documento. */
  criada?: { idOportunidade: number; codigo: string; titulo: string };
  /** "Vincular a ela": quem abriu troca para o dialog de vincular, com esta oportunidade marcada. */
  idOportunidadeParaVincular?: number;
};

/**
 * "Criar oportunidade" pelo documento (RX-01): nasce no CRM com o cliente do documento, na primeira etapa, já
 * vinculada a ele. Se o cliente já tem oportunidade em andamento, avisa sem bloquear e oferece vincular a ela
 * (CA-69). Origem do lead é obrigatória no CRM, por isso o campo existe aqui (o protótipo não o mostra).
 */
@Component({
  selector: 'app-criar-oportunidade-documento-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatProgressSpinnerModule, MatSelectModule, MatTooltipModule, AlertCardComponent, LoadingBoxComponent,
    SelectPesquisavelComponent],
  templateUrl: './criar-oportunidade-documento-dialog.component.html',
  styleUrls: ['./criar-oportunidade-documento-dialog.component.css'],
})
export class CriarOportunidadeDocumentoDialogComponent implements OnInit {
  public readonly tituloControl = new FormControl<string>('', { nonNullable: true, validators: Validators.required });
  public readonly vendedorControl = new FormControl<number | null>(null, Validators.required);
  public readonly origemLeadControl = new FormControl<number | null>(null, Validators.required);

  public origensLead: LookupOportunidade[] = [];
  /** Vendedores que podem ser responsáveis: ativos e dentro do escopo de quem cria, como no CRM. */
  public opcoesVendedor: VendedorLookupStruct[] = [];
  /** Oportunidades em andamento do cliente, para o aviso que não bloqueia. */
  public oportunidadesEmAndamento: OportunidadeResumoStruct[] = [];

  public isLoading = true;
  public isCriando = false;

  constructor(
    private dialogRef: MatDialogRef<CriarOportunidadeDocumentoDialogComponent, CriarOportunidadeDocumentoDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: CriarOportunidadeDocumentoDialogData,
    private processoVendaService: ProcessoVendaService,
    private oportunidadeService: OportunidadeService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    const ehPedido = this.data.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda;
    this.tituloControl.setValue(`${ehPedido ? 'Pedido de venda' : 'Orçamento'} para ${this.data.nomeCliente}`);

    this.carregar();
  }

  /** "Orçamento nº 12 · Cliente": o tipo como o usuário fala, como no protótipo. */
  public get subtitulo(): string {
    const tipo = this.data.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? 'Pedido' : 'Orçamento';
    return `${tipo} nº ${this.data.numero} · ${this.data.nomeCliente}`;
  }

  /** CA-39: o documento sem vendedor exige a escolha do responsável. */
  public get hintVendedor(): string {
    return this.data.idVendedor ? '' : 'O documento não tem vendedor. Escolha o responsável pela oportunidade.';
  }

  public get tituloAvisoEmAndamento(): string {
    const quantidade = this.oportunidadesEmAndamento.length;
    return quantidade === 1
      ? 'Este cliente já tem 1 oportunidade em andamento'
      : `Este cliente já tem ${quantidade} oportunidades em andamento`;
  }

  public get textoAvisoEmAndamento(): string {
    const lista = this.oportunidadesEmAndamento.map(o => `#${o.codigo} ${o.titulo}`).join(' · ');
    return `${lista}. Você pode criar outra mesmo assim.`;
  }

  /** Tooltip do "Criar e vincular" desabilitado: o primeiro campo obrigatório que falta. */
  public get motivoBloqueio(): string {
    if (!this.tituloControl.value.trim()) return 'Informe o assunto.';
    if (this.vendedorControl.value == null) return 'Informe o vendedor responsável.';
    if (this.origemLeadControl.value == null) return 'Informe a origem do lead.';
    return '';
  }

  public fechar(): void {
    if (this.isCriando) return;
    this.dialogRef.close();
  }

  /** "Vincular a ela": a primeira em andamento, que é a mais recente. */
  public irParaVincular(): void {
    if (this.isCriando || !this.oportunidadesEmAndamento.length) return;
    this.dialogRef.close({ idOportunidadeParaVincular: this.oportunidadesEmAndamento[0].idOportunidade });
  }

  public criar(): void {
    if (this.motivoBloqueio || this.isCriando) return;

    const request = new CriarOportunidadeVendaRequest();
    request.titulo = this.tituloControl.value.trim();
    request.idVendedorResponsavel = this.vendedorControl.value!;
    request.idOrigemLead = this.origemLeadControl.value!;

    this.isCriando = true;
    this.processoVendaService.criarOportunidadeDoDocumento(this.data.idVenda, request).subscribe({
      next: response => {
        this.isCriando = false;

        // Criada e não vinculada também volta em isError, com o texto que diz qual oportunidade ficou criada.
        // O documento segue sem vínculo: o dialog fecha para não criar outra por engano.
        if (response.isError || response.idOportunidade == null) {
          this.alertService.show('Aviso', response.errorDescription || 'Não foi possível criar a oportunidade.',
            AlertType.warning);
          if (response.idOportunidade != null) this.dialogRef.close();
          return;
        }

        this.alertService.show('Sucesso',
          `Oportunidade #${response.codigo} criada na primeira etapa e vinculada ao documento. O evento fica no histórico da oportunidade.`,
          AlertType.success);
        this.dialogRef.close({
          criada: { idOportunidade: response.idOportunidade, codigo: response.codigo ?? '', titulo: request.titulo },
        });
      },
      error: () => {
        this.isCriando = false;
        this.alertService.show('Aviso',
          'Não foi possível criar a oportunidade agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  /**
   * Os mesmos lookups da tela do CRM (origens do lead no CRM; vendedores e escopo no orquestrador) e as
   * oportunidades em andamento do cliente. O aviso é acessório: falhar nele não impede criar.
   */
  private carregar(): void {
    forkJoin({
      lookups: this.oportunidadeService.getLookups(),
      configuracoes: this.oportunidadeService.getConfiguracoes(),
      abertas: this.processoVendaService.getOportunidadesAbertasDoCliente(this.data.idPessoaCliente)
        .pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ lookups, configuracoes, abertas }) => {
        this.isLoading = false;

        if (lookups.isError || configuracoes.isError) {
          this.alertService.show('Aviso', lookups.isError ? lookups.errorDescription : configuracoes.errorDescription,
            AlertType.warning);
        }

        this.origensLead = lookups.isError ? [] : lookups.origensLead ?? [];

        const visiveis = configuracoes.isError ? [] : configuracoes.idsVendedoresVisiveis ?? [];
        this.opcoesVendedor = (configuracoes.isError ? [] : configuracoes.vendedores ?? [])
          .filter(v => v.idSituacaoVendedor !== SituacaoVendedorEnum.Inativo
            && (!visiveis.length || visiveis.includes(v.idVendedor)));

        // O vendedor do documento, quando é um dos que podem ser responsáveis.
        const idVendedor = this.data.idVendedor;
        if (idVendedor && this.opcoesVendedor.some(v => v.idVendedor === idVendedor)) {
          this.vendedorControl.setValue(idVendedor);
        }

        this.oportunidadesEmAndamento = abertas && !abertas.isError ? abertas.oportunidades ?? [] : [];
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar os dados da oportunidade agora. Tente novamente em alguns instantes.',
          AlertType.warning);
      },
    });
  }
}
